/**
 * Отбор по диапазону дат ОДНОЙ КНОПКОЙ: готовые окна и календарь месяца.
 *
 * Заведён 15.09.26 для «Планов» (замечание владельца разметкой: «поменяй дату
 * с/по на обычный удобный datepicker»). Два поля `input type="date"` занимали
 * тулбар вдвое, а их календарь браузер рисует СИСТЕМНЫМ попапом — в тёмной
 * теме он белый, ровно та беда, из-за которой списки отбора ушли на `Combo`.
 *
 * КАЛЕНДАРЬ СВОЙ, а не библиотечный: готового нет ни у нас, ни в `urtDesign`,
 * а зависимость ради сетки одного месяца проект не платит. Даты — строками
 * `YYYY-MM-DD` через `lib/dates.ts`: сравнение строк такого вида и есть
 * сравнение дат, а `new Date("YYYY-MM-DD")` уносит день в полночь UTC.
 *
 * ПРАВКА ПРИМЕНЯЕТСЯ ВТОРЫМ КЛИКОМ, без кнопки «применить» — тем же правилом,
 * что у `PeriodPicker`: черновик рядом с настоящим отбором был бы вторым
 * состоянием. Начало, выбранное первым кликом, живёт только пока открыт попап.
 */

import { ChevronDown } from "lucide-react";
import { useRef, useState } from "react";

import { Строка } from "@/components/PeriodPicker";
import { RangeCalendar } from "@/components/RangeCalendar";
import { isValidDate, iso, plusDays } from "@/lib/dates";
import { usePopover } from "@/lib/usePopover";

export type Окно = { label: string; hint: string; from: string; to: string };

/**
 * Готовые окна ПЛАНОВОЙ даты. Она день отгрузки фабрике и чаще лежит в
 * будущем, поэтому окна у «Планов» свои, а не «последние N дней» карточки.
 * Считаются на каждый рендер: «сегодня» меняется в полночь, а вкладку держат
 * днями.
 */
function окна(): Окно[] {
  const today = new Date();
  const t = iso(today);
  return [
    { label: "с сегодня", hint: "плановая дата сегодня и позже", from: t, to: "" },
    {
      label: "ближайшие 30 дней",
      hint: "сегодня и 29 дней вперёд",
      from: t,
      to: iso(plusDays(today, 29)),
    },
    {
      label: "прошлые 30 дней",
      hint: "29 дней назад и сегодня",
      from: iso(plusDays(today, -29)),
      to: t,
    },
    {
      label: "этот месяц",
      hint: "с первого по последнее число",
      from: iso(new Date(today.getFullYear(), today.getMonth(), 1)),
      to: iso(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
    },
  ];
}

/**
 * Готовые окна даты СОЗДАНИЯ. Смотрят назад, в отличие от плановых: «когда
 * набрали план» — вопрос про прошлое, «когда отгружать» — про будущее, и один
 * набор окон на оба врал бы половине нажатий.
 */
export function созданныеОкна(): Окно[] {
  const today = new Date();
  const t = iso(today);
  return [
    { label: "сегодня", hint: "создано сегодня", from: t, to: t },
    {
      label: "7 дней",
      hint: "6 дней назад и сегодня",
      from: iso(plusDays(today, -6)),
      to: t,
    },
    {
      label: "30 дней",
      hint: "29 дней назад и сегодня",
      from: iso(plusDays(today, -29)),
      to: t,
    },
    {
      label: "этот месяц",
      hint: "с первого числа по сегодня",
      from: iso(new Date(today.getFullYear(), today.getMonth(), 1)),
      to: t,
    },
  ];
}

/**
 * «24.09», а у чужого года «24.09.25»: история планов уходит в прошлый год.
 * `сГодом` — год всегда («Приёмка», где история идёт с 2021-го).
 */
function краткая(value: string, сГодом = false): string {
  const d = value.slice(8, 10);
  const m = value.slice(5, 7);
  const y = value.slice(0, 4);
  return !сГодом && y === String(new Date().getFullYear()) ? `${d}.${m}` : `${d}.${m}.${y.slice(2)}`;
}

/** Подпись кнопки: узнанное окно — своим именем, иначе даты. */
function подпись(from: string, to: string, список: Окно[], сГодом: boolean): string {
  if (!from && !to) return "все";
  const узнано = список.find((o) => o.from === from && o.to === to);
  if (узнано) return узнано.label;
  if (!to) return `с ${краткая(from, сГодом)}`;
  if (!from) return `по ${краткая(to, сГодом)}`;
  return `${краткая(from, сГодом)} — ${краткая(to, сГодом)}`;
}

export function DateRangePicker({
  label,
  from: fromRaw,
  to: toRaw,
  onChange,
  windows,
  subject = "плановая дата",
  withAll = true,
  fullYear = false,
  min,
  max,
  maxSpan,
}: {
  label: string;
  /** Строка «все» (снять отбор). Экрану, у которого периода «без границ» нет,
   *  она врёт: «Приёмка» держит своё окно «вся история». */
  withAll?: boolean;
  /** Год в каждой дате подписи, а не только у чужого года. */
  fullYear?: boolean;
  /** Чья это дата — для подсказки кнопки. */
  subject?: string;
  /** Начало включительно, `YYYY-MM-DD`; пустая строка — без нижней границы. */
  from: string;
  /** Конец включительно; пустая строка — без верхней границы. */
  to: string;
  onChange: (from: string, to: string) => void;
  /** Готовые окна попапа. По умолчанию — плановые, смотрящие вперёд. */
  windows?: Окно[];
  /** Границы выбора в календаре включительно; без них выбор свободен. */
  min?: string;
  max?: string;
  /** Самый длинный период в днях: календарь не даст выбрать длиннее. */
  maxSpan?: number;
}) {
  // Даты приходят из адреса, а адрес правят руками: `?from=abc` дал бы месяц
  // «undefined NaN» и сетку из 42 дней `NaN-NaN-NaN`, клик по которой писал бы
  // мусор обратно. Некалендарная граница считается незаданной (финальное
  // ревью 15.09.26).
  const from = isValidDate(fromRaw) ? fromRaw : "";
  const to = isValidDate(toRaw) ? toRaw : "";

  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const список = windows ?? окна();

  // Начатый и брошенный выбор не переживает закрытия: календарь рисуется только
  // открытым, и его состояние умирает вместе с попапом.
  const закрыть = () => setOpen(false);

  // Клик мимо и Esc закрывают попап.
  usePopover(open, закрыть, boxRef);

  const открыть = () => setOpen((было) => !было);

  const выбрать = (nextFrom: string, nextTo: string) => {
    onChange(nextFrom, nextTo);
    закрыть();
  };

  return (
    <div ref={boxRef} className="relative" data-annot={`отбор ${label}`}>
      <button
        type="button"
        onClick={открыть}
        title={
          from || to
            ? `${subject} ${from || "…"} — ${to || "…"} включительно`
            : `${subject} — без отбора`
        }
        className={
          "flex h-8 items-center overflow-hidden rounded-[var(--radius-ui)] border bg-bg text-[13px] " +
          (open ? "border-border-strong" : "border-border hover:border-border-strong")
        }
      >
        <span className="flex h-full flex-none items-center whitespace-nowrap border-r border-border bg-bg-subtle px-2 text-[11.5px] text-fg-faint">
          {label}
        </span>
        <span className="truncate px-2 tabular-nums text-fg">{подпись(from, to, список, fullYear)}</span>
        <ChevronDown size={13} className="mr-1.5 ml-auto flex-none text-fg-faint" />
      </button>

      {open ? (
        <div className="absolute top-[34px] left-0 z-30 w-[264px] overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-elevated shadow-lg">
          <div className="py-1">
            {список.map((окно) => (
              <Строка
                key={окно.label}
                label={окно.label}
                title={окно.hint}
                on={окно.from === from && окно.to === to}
                onClick={() => выбрать(окно.from, окно.to)}
              />
            ))}
            {withAll ? (
              <Строка
                label="все"
                title="снять отбор по дате"
                on={!from && !to}
                onClick={() => выбрать("", "")}
              />
            ) : null}
          </div>

          <div className="border-t border-border">
            <RangeCalendar from={from} to={to} onPick={выбрать} min={min} max={max} maxSpan={maxSpan} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
